import type { CreateDocumentRequest } from '@palaccount/contracts';
import type { DocumentPayload } from '@palaccount/documents';
import { FileDown, Loader2, Printer } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { printDocument, usePrintOptions } from '@/lib/print-document';
import { useMe } from '@/lib/session';

import { downloadDocument } from './api';

/**
 * The Print and PDF pair, for any screen that has a document behind it —
 * SAL-04's invoice, CRM-04's statement.
 *
 * ── The two buttons do the same thing by two routes, on purpose ────────────
 * PRINT renders `renderDocumentHtml(payload)` in the browser and prints it.
 * PDF asks the worker, which renders `renderDocumentHtml(payload)` in
 * Chromium and returns bytes. SAME FUNCTION, so the paper in a customer's
 * hand and the PDF in their inbox are the same document — which is the
 * property DECISION.md §2.13 bought when it chose a browser engine over a
 * drawing API, and which no amount of care at two call sites would keep true.
 *
 * ── Why Print does not go through the worker ───────────────────────────────
 * It could, and it would be slower for no gain: the browser already has the
 * data on screen and already contains the engine. Printing locally is instant;
 * a PDF is a job because a FILE has to be produced, stored and downloaded, and
 * §2.13 is explicit that a request must not wait on Chromium.
 *
 * The PDF is nonetheless the server's own reading of the record, not the
 * screen's: the request carries an ID, and the worker re-reads the invoice.
 * A customer cannot be sent a PDF of numbers a browser made up.
 */
export function DocumentActions({
  request,
  payload,
  disabled,
}: {
  /** What to ask the worker for — ids only for an invoice or a statement. */
  request: CreateDocumentRequest;
  /** The same document, for the instant local print. `null` while loading. */
  payload: DocumentPayload | null;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  // INV-H6 — the company's print options (header text, hidden columns, paper,
  // language), layered over the shared template at print time.
  const printOptions = usePrintOptions();
  const [state, setState] = useState<'idle' | 'working' | 'failed'>('idle');
  const locale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);

  return (
    <>
      <Button
        variant="outline"
        disabled={disabled || payload === null}
        onClick={() =>
          payload &&
          printDocument(
            {
              locale,
              company: { name: me?.tenant.name ?? '' },
              generatedAt: new Date().toISOString(),
              // NFR-33 — the person producing the print is the signed-in user.
              generatedBy: me?.user.name ?? null,
              payload,
            },
            printOptions,
          )
        }
      >
        <Printer aria-hidden /> {t('common.print')}
      </Button>
      <Button
        variant="outline"
        disabled={disabled || state === 'working'}
        onClick={async () => {
          setState('working');
          try {
            await downloadDocument(request);
            setState('idle');
          } catch {
            // The row keeps the reason and the sweep keeps trying; what the
            // user needs here is to know the click did not work.
            setState('failed');
          }
        }}
      >
        {state === 'working' ? <Loader2 className="animate-spin" aria-hidden /> : <FileDown aria-hidden />}
        {t('common.pdf')}
      </Button>
      {state === 'failed' ? (
        <span role="alert" className="self-center text-xs text-destructive">
          {t('common.pdfFailed')}
        </span>
      ) : null}
    </>
  );
}
